import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { 
  User, 
  AuthState, 
  LoginRequest, 
  RegisterRequest, 
  LoginResponse,
  ChangePasswordRequest,
  UpdateProfileRequest 
} from '../types/auth';
import { config } from '../config/env';

interface AuthActions {
  login: (credentials: LoginRequest) => Promise<void>;
  register: (userData: RegisterRequest) => Promise<void>;
  logout: () => void;
  updateProfile: (data: UpdateProfileRequest) => Promise<void>;
  changePassword: (data: ChangePasswordRequest) => Promise<void>;
  verifyToken: () => Promise<boolean>;
  clearError: () => void;
  setToken: (token: string) => void;
  setUser: (user: User) => void;
}

interface AuthStore extends AuthState, AuthActions {}

const API_BASE_URL = config.apiBaseUrl;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // State
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Actions
      login: async (credentials: LoginRequest) => {
        try {
          set({ isLoading: true, error: null });

          const response = await fetch(`${API_BASE_URL}/auth/login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(credentials),
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.message || 'Login failed');
          }

          const { user, token }: LoginResponse = data.data;

          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          if (config.enableDebugLogs) {
            console.log('✅ User logged in successfully:', user.email);
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Login failed';
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: errorMessage,
          });
          throw error;
        }
      },

      register: async (userData: RegisterRequest) => {
        try {
          set({ isLoading: true, error: null });

          const response = await fetch(`${API_BASE_URL}/auth/register`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(userData),
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.message || 'Registration failed');
          }

          const { user, token }: LoginResponse = data.data;

          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          if (config.enableDebugLogs) {
            console.log('✅ User registered successfully:', user.email);
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Registration failed';
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: errorMessage,
          });
          throw error;
        }
      },

      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });

        if (config.enableDebugLogs) {
          console.log('👋 User logged out');
        }
      },

      updateProfile: async (data: UpdateProfileRequest) => {
        try {
          const { token } = get();
          if (!token) {
            throw new Error('No authentication token');
          }

          set({ isLoading: true, error: null });

          const response = await fetch(`${API_BASE_URL}/auth/profile`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(data),
          });

          const responseData = await response.json();

          if (!response.ok) {
            throw new Error(responseData.message || 'Profile update failed');
          }

          const updatedUser: User = responseData.data;

          set({
            user: updatedUser,
            isLoading: false,
            error: null,
          });

          if (config.enableDebugLogs) {
            console.log('✅ Profile updated successfully');
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Profile update failed';
          set({
            isLoading: false,
            error: errorMessage,
          });
          throw error;
        }
      },

      changePassword: async (data: ChangePasswordRequest) => {
        try {
          const { token } = get();
          if (!token) {
            throw new Error('No authentication token');
          }

          set({ isLoading: true, error: null });

          const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(data),
          });

          const responseData = await response.json();

          if (!response.ok) {
            throw new Error(responseData.message || 'Password change failed');
          }

          set({
            isLoading: false,
            error: null,
          });

          if (config.enableDebugLogs) {
            console.log('✅ Password changed successfully');
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Password change failed';
          set({
            isLoading: false,
            error: errorMessage,
          });
          throw error;
        }
      },

      verifyToken: async (): Promise<boolean> => {
        try {
          const { token } = get();
          if (!token) {
            return false;
          }

          const response = await fetch(`${API_BASE_URL}/auth/verify-token`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (!response.ok) {
            // Token is invalid, clear auth state
            get().logout();
            return false;
          }

          const data = await response.json();
          if (data.success && data.data.valid) {
            // Update user data if needed
            const currentUser = get().user;
            const tokenUser = data.data.user;
            
            if (currentUser && JSON.stringify(currentUser) !== JSON.stringify(tokenUser)) {
              set({ user: tokenUser });
            }
            
            return true;
          }

          return false;
        } catch (error) {
          if (config.enableDebugLogs) {
            console.warn('Token verification failed:', error);
          }
          return false;
        }
      },

      setToken: (token: string) => {
        set({ token });
      },

      setUser: (user: User) => {
        set({ user, isAuthenticated: true });
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'bike-tracker-auth', // Storage key
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Only persist these fields
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Reset loading states on rehydration
        if (state) {
          state.isLoading = false;
          state.error = null;
        }
        
        if (config.enableDebugLogs && state?.user) {
          console.log('🔄 Auth state rehydrated for:', state.user.email);
        }
      },
    }
  )
);